import {
  faArrowRotateRight,
  faCircleExclamation,
  faFileInvoice,
  faMagnifyingGlass,
  faPeopleArrowsLeftRight,
  faReceipt,
} from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useMemo, useState } from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import Chip from '@/components/Chip';
import EmptyState from '@/components/EmptyState';
import IconicButton from '@/components/IconicButton';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import Tabs from '@/components/Tabs';
import TextInput from '@/components/TextInput';
import TextStack from '@/components/TextStack';
import { Body, Heading, Small } from '@/components/Typography';
import { TableCell, TableRow } from '@/elements/Table';
import Link from '@/elements/Link';
import Icon from '@/elements/Icon';
import useMediaQuery from '@/hooks/useMediaQuery';
import ROUTES from '@/routes/routes';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type SummaryResponse = {
  status: string;
  timestamp: string;
  data: {
    filters: {
      account_id?: string | null;
      customer_id?: string | null;
      timesheet_id?: string | null;
      invoice_id?: string | null;
      from?: string;
      to?: string;
      limit?: number;
    };
    summary: {
      timesheets_analyzed: number;
      timesheet_anomalies: number;
      invoices_analyzed: number;
      invoice_anomalies: number;
      duplicate_invoice_groups: number;
      duplicate_invoice_item_groups: number;
      candidate_duplicate_pairs: number;
      local_empty_stripe_populated_invoices: number;
      net_time_mismatches: number;
      critical_anomalies: number;
    };
    timesheet_anomalies: TimesheetAnomaly[];
    invoice_anomalies: InvoiceAnomaly[];
    duplicate_invoice_groups: DuplicateInvoiceGroup[];
    duplicate_invoice_item_groups: DuplicateInvoiceItemGroup[];
    candidate_duplicate_pairs: CandidateDuplicatePair[];
    local_empty_stripe_populated_invoices: InvoiceAnomaly[];
  };
};

type TimesheetAnomaly = {
  timesheet: {
    id: number;
    account_id: number;
    customer_id: number;
    order_id: number | null;
    job_id: number | null;
    invoiced: boolean;
    stripe_invoice_item_id: string | null;
  };
  status: string;
  recommended_action: string;
  issues: string[];
  keeper_invoice_item_id: number | null;
  orphan_invoice_item_id: number | null;
  net_time_analysis?: {
    expected_net_time: number;
    actual_net_time: number;
    difference_minutes: number;
    status: string;
    direction: string;
  } | null;
  active_invoice_items: Array<{
    id: number;
    invoice_id: number | null;
    stripe_invoice_item_id: string | null;
    stripe_invoice_id: string | null;
    net_time?: number | null;
  }>;
};

type InvoiceAnomaly = {
  invoice: {
    id: number;
    account_id: number | null;
    customer_id: number | null;
    stripe_invoice_id: string | null;
    status: string;
    deleted: boolean;
  };
  status: string;
  issues: string[];
  stripe_items_missing_locally: string[];
  local_items_missing_in_stripe: string[];
  net_time_anomalies: Array<{
    timesheet_id: number;
    expected_net_time: number;
    actual_net_time: number;
    difference_minutes: number;
    status: string;
    direction: string;
    invoice_item_ids: number[];
  }>;
  net_time_minor_variances: Array<{
    timesheet_id: number;
    expected_net_time: number;
    actual_net_time: number;
    difference_minutes: number;
    status: string;
    direction: string;
    invoice_item_ids: number[];
  }>;
  duplicate_local_invoices: Array<{ id: number; status: string }>;
  local_invoice_items: Array<{
    id: number;
    timesheet_id: number | null;
    stripe_invoice_item_id: string | null;
    invoice_id: number | null;
    net_time?: number | null;
  }>;
  stripe_invoice_items: Array<{
    id: string;
    invoice: string | null;
    amount: number | null;
    description: string | null;
  }>;
};

type DuplicateInvoiceGroup = {
  stripe_invoice_id: string;
  invoice_ids: number[];
  account_ids: number[];
  customer_ids: number[];
  statuses: string[];
};

type DuplicateInvoiceItemGroup = {
  stripe_invoice_item_id: string;
  invoice_item_ids: number[];
  invoice_ids: number[];
  timesheet_ids: number[];
};

type CandidateDuplicatePair = {
  left_invoice_id: number;
  right_invoice_id: number;
  shared_stripe_item_count: number;
  left_only_count: number;
  right_only_count: number;
  fully_covered: boolean;
  duplicate_invoice_id: number | null;
  keeper_invoice_id: number | null;
};

type DetailState =
  | { kind: 'timesheet'; payload: TimesheetAnomaly | null }
  | { kind: 'invoice'; payload: InvoiceAnomaly | null }
  | { kind: 'duplicate-pair'; payload: CandidateDuplicatePair | null }
  | { kind: 'duplicate-group'; payload: DuplicateInvoiceGroup | null }
  | { kind: 'duplicate-item-group'; payload: DuplicateInvoiceItemGroup | null };

type TabKey = 'overview' | 'timesheets' | 'invoices' | 'duplicates';

const SUMMARY_CARDS: Array<{
  key: keyof SummaryResponse['data']['summary'];
  label: string;
  icon: any;
}> = [
  {
    key: 'critical_anomalies',
    label: 'Critical Anomalies',
    icon: faCircleExclamation,
  },
  { key: 'timesheet_anomalies', label: 'Broken Timesheets', icon: faReceipt },
  {
    key: 'invoice_anomalies',
    label: 'Invoice Sync Issues',
    icon: faFileInvoice,
  },
  { key: 'net_time_mismatches', label: 'Net Time Mismatches', icon: faReceipt },
  {
    key: 'duplicate_invoice_groups',
    label: 'Duplicate Invoices',
    icon: faPeopleArrowsLeftRight,
  },
  {
    key: 'duplicate_invoice_item_groups',
    label: 'Duplicate Invoice Items',
    icon: faPeopleArrowsLeftRight,
  },
  {
    key: 'local_empty_stripe_populated_invoices',
    label: 'Stripe Populated Local Empty',
    icon: faFileInvoice,
  },
];

const TIMESHEET_FIELDS = [
  { name: 'TIMESHEET', style: { width: '10%' } },
  { name: 'ACCOUNT', style: { width: '10%', color: 'black' } },
  { name: 'STATUS', style: { width: '16%', color: 'black' } },
  { name: 'ACTION', style: { width: '16%', color: 'black' } },
  { name: 'LINK STATE', style: { width: '22%', color: 'black' } },
  { name: 'NET TIME', style: { width: '16%', color: 'black' } },
  { name: 'DETAILS', style: { width: '10%', color: 'black' } },
];

const INVOICE_FIELDS = [
  { name: 'INVOICE', style: { width: '10%' } },
  { name: 'ACCOUNT', style: { width: '10%', color: 'black' } },
  { name: 'STATUS', style: { width: '14%', color: 'black' } },
  { name: 'ISSUES', style: { width: '32%', color: 'black' } },
  { name: 'COUNTS', style: { width: '20%', color: 'black' } },
  { name: 'DETAILS', style: { width: '14%', color: 'black' } },
];

const DISCREPANCY_FIELDS = [
  { name: 'INVOICE', style: { width: '10%' } },
  { name: 'ACCOUNT', style: { width: '10%', color: 'black' } },
  { name: 'STATUS', style: { width: '14%', color: 'black' } },
  { name: 'MAJOR / MINOR', style: { width: '18%', color: 'black' } },
  { name: 'AFFECTED TIMESHEETS', style: { width: '26%', color: 'black' } },
  { name: 'DETAILS', style: { width: '12%', color: 'black' } },
  { name: 'OPEN', style: { width: '10%', color: 'black' } },
];

const DUPLICATE_PAIR_FIELDS = [
  { name: 'DUPLICATE', style: { width: '14%' } },
  { name: 'KEEPER', style: { width: '14%', color: 'black' } },
  { name: 'SHARED ITEMS', style: { width: '14%', color: 'black' } },
  { name: 'COVERAGE', style: { width: '14%', color: 'black' } },
  { name: 'LEFT / RIGHT ONLY', style: { width: '18%', color: 'black' } },
  { name: 'DETAILS', style: { width: '12%', color: 'black' } },
  { name: 'OPEN', style: { width: '14%', color: 'black' } },
];

const DUPLICATE_GROUP_FIELDS = [
  { name: 'STRIPE INVOICE', style: { width: '30%' } },
  { name: 'LOCAL INVOICES', style: { width: '26%', color: 'black' } },
  { name: 'ACCOUNTS', style: { width: '14%', color: 'black' } },
  { name: 'STATUSES', style: { width: '18%', color: 'black' } },
  { name: 'DETAILS', style: { width: '12%', color: 'black' } },
];

const DUPLICATE_ITEM_FIELDS = [
  { name: 'STRIPE ITEM', style: { width: '28%' } },
  { name: 'LOCAL ITEMS', style: { width: '18%', color: 'black' } },
  { name: 'INVOICES', style: { width: '18%', color: 'black' } },
  { name: 'TIMESHEETS', style: { width: '24%', color: 'black' } },
  { name: 'DETAILS', style: { width: '12%', color: 'black' } },
];

const TABS = [
  { id: 'overview', label: 'Overview', a11yLabel: 'Overview' },
  { id: 'timesheets', label: 'Timesheets', a11yLabel: 'Timesheet anomalies' },
  { id: 'invoices', label: 'Invoices', a11yLabel: 'Invoice anomalies' },
  { id: 'duplicates', label: 'Duplicates', a11yLabel: 'Duplicate diagnostics' },
];

const DEFAULT_FILTERS = {
  accountId: '',
  customerId: '',
  timesheetId: '',
  invoiceId: '',
  from: '',
  to: '',
  limit: '50',
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return 'n/a';

  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const compactList = (
  values: Array<string | number | null | undefined>,
  max = 4,
) => {
  const safeValues = values.filter(
    (value): value is string | number => value !== null && value !== undefined,
  );

  if (safeValues.length === 0) return 'n/a';

  const visible = safeValues.slice(0, max).join(', ');
  const hiddenCount = safeValues.length - max;

  return hiddenCount > 0 ? `${visible} +${hiddenCount} more` : visible;
};

const humanizeIssue = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const toneForIssue = (issue: string) => {
  if (
    issue.includes('missing') ||
    issue.includes('deleted') ||
    issue.includes('mismatch')
  ) {
    return 'error';
  }

  if (issue.includes('duplicate') || issue.includes('shared')) {
    return 'warning';
  }

  return 'default';
};

const issueChips = (issues: string[]) => {
  if (issues.length === 0) {
    return <Chip color="success" label="Healthy" size="small" />;
  }

  return (
    <Stack gap={6} wrap>
      {issues.map((issue) => (
        <Chip
          key={issue}
          color={toneForIssue(issue)}
          label={humanizeIssue(issue)}
          size="small"
        />
      ))}
    </Stack>
  );
};

const statusChip = (value: string) => {
  if (value === 'valid' || value === 'in_sync') {
    return <Chip color="success" label={value} size="small" />;
  }

  if (value.includes('manual_review') || value.includes('warning')) {
    return <Chip color="warning" label={value} size="small" />;
  }

  return <Chip color="error" label={value} size="small" />;
};

const healthTone = (summary?: SummaryResponse['data']['summary']) => {
  if (!summary) {
    return { label: 'Unknown', color: 'default' as const };
  }

  if (summary.critical_anomalies > 0) {
    return { label: 'Critical', color: 'error' as const };
  }

  if (
    summary.timesheet_anomalies > 0 ||
    summary.invoice_anomalies > 0 ||
    summary.candidate_duplicate_pairs > 0
  ) {
    return { label: 'Needs Review', color: 'warning' as const };
  }

  return { label: 'Healthy', color: 'success' as const };
};

const keyValueRow = (label: string, value: React.ReactNode) => (
  <Stack key={label} justify="between" align="start" gap={12}>
    <Small style={{ color: '#667085', minWidth: 120 }}>{label}</Small>
    <Small style={{ textAlign: 'right', wordBreak: 'break-word' }}>
      {value}
    </Small>
  </Stack>
);

const routeToInvoice = (invoiceId: number) =>
  `/${ROUTES.invoices}/${invoiceId}`;

const InvoiceHealth = () => {
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SummaryResponse['data'] | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState>({
    kind: 'timesheet',
    payload: null,
  });

  useEffect(() => {
    fetchSummary();
  }, []);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.accountId) params.append('account_id', filters.accountId);
    if (filters.customerId) params.append('customer_id', filters.customerId);
    if (filters.timesheetId) params.append('timesheet_id', filters.timesheetId);
    if (filters.invoiceId) params.append('invoice_id', filters.invoiceId);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.limit) params.append('limit', filters.limit);
    return params.toString();
  }, [filters]);

  const activeFilterCount = useMemo(
    () =>
      [
        filters.accountId,
        filters.customerId,
        filters.timesheetId,
        filters.invoiceId,
        filters.from,
        filters.to,
      ].filter(Boolean).length,
    [filters],
  );

  const discrepancyInvoices = useMemo(
    () =>
      data?.invoice_anomalies.filter(
        (item) =>
          item.net_time_anomalies.length > 0 ||
          item.net_time_minor_variances.length > 0,
      ) || [],
    [data],
  );

  const topTimesheets = useMemo(
    () => data?.timesheet_anomalies.slice(0, 5) || [],
    [data],
  );
  const topInvoices = useMemo(
    () => data?.invoice_anomalies.slice(0, 5) || [],
    [data],
  );
  const topDuplicatePairs = useMemo(
    () => data?.candidate_duplicate_pairs.slice(0, 5) || [],
    [data],
  );

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await axiosRubyClient.get<SummaryResponse>(
        `/api/invoice_health/summary${queryString ? `?${queryString}` : ''}`,
      );
      setData(response.data.data);
      setLastUpdated(response.data.timestamp || new Date().toISOString());
      setError(null);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to load invoice health',
      );
    } finally {
      setLoading(false);
    }
  };

  const onFilterChange = (field: keyof typeof filters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const selectedTab = TABS[activeTab]?.id as TabKey;
  const summaryTone = healthTone(data?.summary);

  const linkedInvoiceIdsForTimesheet = (item: TimesheetAnomaly) =>
    Array.from(
      new Set(
        item.active_invoice_items
          .map((row) => row.invoice_id)
          .filter((value): value is number => Boolean(value)),
      ),
    );

  const renderOverview = () => (
    <Stack vertical gap={24}>
      <Card>
        <Card.Section>
          <Heading>Priority Queue</Heading>
          <Small>
            Start with critical anomalies, then move to net-time discrepancies
            and duplicate candidates.
          </Small>
        </Card.Section>
        <Card.Section>
          <Stack gap={16} wrap>
            <div style={{ minWidth: 180, flex: 1 }}>
              <TextStack spacing="tight">
                <Body weight="semibold">Critical anomalies</Body>
                <Small>
                  {data?.summary.critical_anomalies || 0} require attention
                </Small>
              </TextStack>
            </div>
            <div style={{ minWidth: 180, flex: 1 }}>
              <TextStack spacing="tight">
                <Body weight="semibold">Net-time mismatch invoices</Body>
                <Small>
                  {discrepancyInvoices.length} invoices with discrepancy cases
                </Small>
              </TextStack>
            </div>
            <div style={{ minWidth: 180, flex: 1 }}>
              <TextStack spacing="tight">
                <Body weight="semibold">Duplicate candidates</Body>
                <Small>
                  {data?.candidate_duplicate_pairs.length || 0} likely duplicate
                  invoice pairs
                </Small>
              </TextStack>
            </div>
          </Stack>
        </Card.Section>
      </Card>

      <Stack gap={24} wrap>
        <div style={{ flex: 1, minWidth: 280 }}>
          <Card>
            <Card.Section>
              <Heading>Top Timesheet Cases</Heading>
            </Card.Section>
            <Card.Section>
              {topTimesheets.length === 0 ? (
                <EmptyState title="No timesheet anomalies" />
              ) : (
                <Stack vertical gap={12}>
                  {topTimesheets.map((item) => (
                    <Stack
                      key={`top-timesheet-${item.timesheet.id}`}
                      justify="between"
                      align="start"
                      gap={12}
                    >
                      <TextStack spacing="tight">
                        <Body weight="semibold">
                          Timesheet {item.timesheet.id}
                        </Body>
                        <Small>
                          Account {item.timesheet.account_id} •{' '}
                          {item.recommended_action}
                        </Small>
                      </TextStack>
                      <IconicButton
                        a11yLabel={`Inspect top timesheet ${item.timesheet.id}`}
                        icon={faMagnifyingGlass}
                        text="Inspect"
                        onClick={() =>
                          setDetail({ kind: 'timesheet', payload: item })
                        }
                      />
                    </Stack>
                  ))}
                </Stack>
              )}
            </Card.Section>
          </Card>
        </div>

        <div style={{ flex: 1, minWidth: 280 }}>
          <Card>
            <Card.Section>
              <Heading>Top Invoice Cases</Heading>
            </Card.Section>
            <Card.Section>
              {topInvoices.length === 0 ? (
                <EmptyState title="No invoice sync anomalies" />
              ) : (
                <Stack vertical gap={12}>
                  {topInvoices.map((item) => (
                    <Stack
                      key={`top-invoice-${item.invoice.id}`}
                      justify="between"
                      align="start"
                      gap={12}
                    >
                      <TextStack spacing="tight">
                        <Body weight="semibold">Invoice {item.invoice.id}</Body>
                        <Small>
                          {item.issues.length} issue
                          {item.issues.length === 1 ? '' : 's'} • account{' '}
                          {item.invoice.account_id || 'n/a'}
                        </Small>
                      </TextStack>
                      <IconicButton
                        a11yLabel={`Inspect top invoice ${item.invoice.id}`}
                        icon={faMagnifyingGlass}
                        text="Inspect"
                        onClick={() =>
                          setDetail({ kind: 'invoice', payload: item })
                        }
                      />
                    </Stack>
                  ))}
                </Stack>
              )}
            </Card.Section>
          </Card>
        </div>

        <div style={{ flex: 1, minWidth: 280 }}>
          <Card>
            <Card.Section>
              <Heading>Top Duplicate Cases</Heading>
            </Card.Section>
            <Card.Section>
              {topDuplicatePairs.length === 0 ? (
                <EmptyState title="No duplicate invoice pairs" />
              ) : (
                <Stack vertical gap={12}>
                  {topDuplicatePairs.map((item) => (
                    <Stack
                      key={`top-dup-${item.left_invoice_id}-${item.right_invoice_id}`}
                      justify="between"
                      align="start"
                      gap={12}
                    >
                      <TextStack spacing="tight">
                        <Body weight="semibold">
                          {item.duplicate_invoice_id || item.left_invoice_id} vs{' '}
                          {item.keeper_invoice_id || item.right_invoice_id}
                        </Body>
                        <Small>
                          shared {item.shared_stripe_item_count} •{' '}
                          {item.fully_covered ? 'fully covered' : 'partial'}
                        </Small>
                      </TextStack>
                      <IconicButton
                        a11yLabel={`Inspect duplicate pair ${item.left_invoice_id} ${item.right_invoice_id}`}
                        icon={faMagnifyingGlass}
                        text="Inspect"
                        onClick={() =>
                          setDetail({
                            kind: 'duplicate-pair',
                            payload: item,
                          })
                        }
                      />
                    </Stack>
                  ))}
                </Stack>
              )}
            </Card.Section>
          </Card>
        </div>
      </Stack>
    </Stack>
  );

  const renderTimesheets = () => (
    <Card>
      <Card.Section>
        <Heading>Timesheet Anomalies</Heading>
        <Small>
          Focus here when the timesheet pointer, local invoice-item state, or
          billed net time looks suspicious.
        </Small>
      </Card.Section>
      {data?.timesheet_anomalies.length === 0 ? (
        <Card.Section>
          <EmptyState title="No broken timesheets" />
        </Card.Section>
      ) : (
        <Table>
          <TableHeader fields={TIMESHEET_FIELDS} />
          <tbody>
            {data?.timesheet_anomalies.map((item) => {
              const linkedInvoiceIds = linkedInvoiceIdsForTimesheet(item);
              const netTimeSummary = item.net_time_analysis
                ? `${item.net_time_analysis.actual_net_time}m / ${item.net_time_analysis.expected_net_time}m`
                : 'n/a';

              return (
                <TableRow key={item.timesheet.id}>
                  <TableCell>{item.timesheet.id}</TableCell>
                  <TableCell>{item.timesheet.account_id}</TableCell>
                  <TableCell>{statusChip(item.status)}</TableCell>
                  <TableCell>{item.recommended_action}</TableCell>
                  <TableCell>
                    keeper {item.keeper_invoice_item_id || 'n/a'}
                    <br />
                    orphan {item.orphan_invoice_item_id || 'n/a'}
                    <br />
                    invoices {compactList(linkedInvoiceIds)}
                  </TableCell>
                  <TableCell>{netTimeSummary}</TableCell>
                  <TableCell>
                    <IconicButton
                      a11yLabel={`Inspect timesheet ${item.timesheet.id}`}
                      icon={faMagnifyingGlass}
                      text="Inspect"
                      onClick={() =>
                        setDetail({
                          kind: 'timesheet',
                          payload: item,
                        })
                      }
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      )}
    </Card>
  );

  const renderInvoices = () => (
    <Stack vertical gap={24}>
      <Card>
        <Card.Section>
          <Heading>Invoice Discrepancies</Heading>
          <Small>
            Diagnose invoices where local line-item net time does not line up
            with approved billed minutes on linked timesheets.
          </Small>
        </Card.Section>
        {discrepancyInvoices.length === 0 ? (
          <Card.Section>
            <EmptyState title="No invoice discrepancy cases found" />
          </Card.Section>
        ) : (
          <Table>
            <TableHeader fields={DISCREPANCY_FIELDS} />
            <tbody>
              {discrepancyInvoices.map((item) => (
                <TableRow key={`discrepancy-${item.invoice.id}`}>
                  <TableCell>
                    <Link to={routeToInvoice(item.invoice.id)}>
                      {item.invoice.id}
                    </Link>
                  </TableCell>
                  <TableCell>{item.invoice.account_id || 'n/a'}</TableCell>
                  <TableCell>{statusChip(item.status)}</TableCell>
                  <TableCell>
                    major {item.net_time_anomalies.length}
                    <br />
                    minor {item.net_time_minor_variances.length}
                  </TableCell>
                  <TableCell>
                    {compactList(
                      item.net_time_anomalies.length > 0
                        ? item.net_time_anomalies.map(
                            (entry) => entry.timesheet_id,
                          )
                        : item.net_time_minor_variances.map(
                            (entry) => entry.timesheet_id,
                          ),
                      6,
                    )}
                  </TableCell>
                  <TableCell>
                    <IconicButton
                      a11yLabel={`Inspect discrepancy invoice ${item.invoice.id}`}
                      icon={faMagnifyingGlass}
                      text="Inspect"
                      onClick={() =>
                        setDetail({ kind: 'invoice', payload: item })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Link to={routeToInvoice(item.invoice.id)}>Invoice</Link>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <Card.Section>
          <Heading>Invoice Sync Anomalies</Heading>
          <Small>
            Local versus Stripe mismatches, missing line items, or deleted
            Stripe invoices that still look active locally.
          </Small>
        </Card.Section>
        {data?.invoice_anomalies.length === 0 ? (
          <Card.Section>
            <EmptyState title="No invoice sync anomalies" />
          </Card.Section>
        ) : (
          <Table>
            <TableHeader fields={INVOICE_FIELDS} />
            <tbody>
              {data?.invoice_anomalies.map((item) => (
                <TableRow key={item.invoice.id}>
                  <TableCell>
                    <Link to={routeToInvoice(item.invoice.id)}>
                      {item.invoice.id}
                    </Link>
                  </TableCell>
                  <TableCell>{item.invoice.account_id || 'n/a'}</TableCell>
                  <TableCell>{statusChip(item.status)}</TableCell>
                  <TableCell>{issueChips(item.issues)}</TableCell>
                  <TableCell>
                    local items {item.local_invoice_items.length}
                    <br />
                    stripe items {item.stripe_invoice_items.length}
                    <br />
                    net mismatches {item.net_time_anomalies.length}
                  </TableCell>
                  <TableCell>
                    <IconicButton
                      a11yLabel={`Inspect invoice ${item.invoice.id}`}
                      icon={faMagnifyingGlass}
                      text="Inspect"
                      onClick={() =>
                        setDetail({ kind: 'invoice', payload: item })
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Stack>
  );

  const renderDuplicates = () => (
    <Stack vertical gap={24}>
      <Card>
        <Card.Section>
          <Heading>Duplicate Invoice Candidates</Heading>
          <Small>
            Stripe item overlap between local invoices. Fully covered draft
            duplicates are usually the safest cleanup queue.
          </Small>
        </Card.Section>
        {data?.candidate_duplicate_pairs.length === 0 ? (
          <Card.Section>
            <EmptyState title="No duplicate invoice pairs detected" />
          </Card.Section>
        ) : (
          <Table>
            <TableHeader fields={DUPLICATE_PAIR_FIELDS} />
            <tbody>
              {data?.candidate_duplicate_pairs.map((item) => (
                <TableRow
                  key={`${item.left_invoice_id}-${item.right_invoice_id}`}
                >
                  <TableCell>{item.duplicate_invoice_id || 'n/a'}</TableCell>
                  <TableCell>{item.keeper_invoice_id || 'n/a'}</TableCell>
                  <TableCell>{item.shared_stripe_item_count}</TableCell>
                  <TableCell>
                    {item.fully_covered ? (
                      <Chip
                        color="warning"
                        label="Fully Covered"
                        size="small"
                      />
                    ) : (
                      <Chip color="default" label="Partial" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    left {item.left_only_count} / right {item.right_only_count}
                  </TableCell>
                  <TableCell>
                    <IconicButton
                      a11yLabel={`Inspect duplicate pair ${item.left_invoice_id} ${item.right_invoice_id}`}
                      icon={faMagnifyingGlass}
                      text="Inspect"
                      onClick={() =>
                        setDetail({
                          kind: 'duplicate-pair',
                          payload: item,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {item.duplicate_invoice_id ? (
                      <Link to={routeToInvoice(item.duplicate_invoice_id)}>
                        Duplicate
                      </Link>
                    ) : (
                      'n/a'
                    )}
                    <br />
                    {item.keeper_invoice_id ? (
                      <Link to={routeToInvoice(item.keeper_invoice_id)}>
                        Keeper
                      </Link>
                    ) : (
                      'n/a'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <Card.Section>
          <Heading>Duplicate Local Invoice Rows</Heading>
          <Small>
            Multiple local invoices sharing the same Stripe invoice id.
          </Small>
        </Card.Section>
        {data?.duplicate_invoice_groups.length === 0 ? (
          <Card.Section>
            <EmptyState title="No duplicate local invoice groups" />
          </Card.Section>
        ) : (
          <Table>
            <TableHeader fields={DUPLICATE_GROUP_FIELDS} />
            <tbody>
              {data?.duplicate_invoice_groups.map((item) => (
                <TableRow key={item.stripe_invoice_id}>
                  <TableCell>{item.stripe_invoice_id}</TableCell>
                  <TableCell>{compactList(item.invoice_ids, 6)}</TableCell>
                  <TableCell>{compactList(item.account_ids, 4)}</TableCell>
                  <TableCell>{compactList(item.statuses, 4)}</TableCell>
                  <TableCell>
                    <IconicButton
                      a11yLabel={`Inspect duplicate invoice group ${item.stripe_invoice_id}`}
                      icon={faMagnifyingGlass}
                      text="Inspect"
                      onClick={() =>
                        setDetail({
                          kind: 'duplicate-group',
                          payload: item,
                        })
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <Card.Section>
          <Heading>Duplicate Local Invoice Items</Heading>
          <Small>
            Local invoice-item rows reusing the same Stripe invoice item id.
          </Small>
        </Card.Section>
        {data?.duplicate_invoice_item_groups.length === 0 ? (
          <Card.Section>
            <EmptyState title="No duplicate invoice-item groups" />
          </Card.Section>
        ) : (
          <Table>
            <TableHeader fields={DUPLICATE_ITEM_FIELDS} />
            <tbody>
              {data?.duplicate_invoice_item_groups.map((item) => (
                <TableRow key={item.stripe_invoice_item_id}>
                  <TableCell>{item.stripe_invoice_item_id}</TableCell>
                  <TableCell>{compactList(item.invoice_item_ids, 5)}</TableCell>
                  <TableCell>{compactList(item.invoice_ids, 5)}</TableCell>
                  <TableCell>{compactList(item.timesheet_ids, 5)}</TableCell>
                  <TableCell>
                    <IconicButton
                      a11yLabel={`Inspect duplicate invoice item group ${item.stripe_invoice_item_id}`}
                      icon={faMagnifyingGlass}
                      text="Inspect"
                      onClick={() =>
                        setDetail({
                          kind: 'duplicate-item-group',
                          payload: item,
                        })
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Stack>
  );

  const renderTimesheetDetail = (payload: TimesheetAnomaly) => {
    const linkedInvoiceIds = linkedInvoiceIdsForTimesheet(payload);

    return (
      <Stack vertical gap={16}>
        <TextStack spacing="tight">
          <Body weight="semibold">Timesheet {payload.timesheet.id}</Body>
          <Small>
            Account {payload.timesheet.account_id} • Customer{' '}
            {payload.timesheet.customer_id}
          </Small>
        </TextStack>

        {statusChip(payload.status)}
        {issueChips(payload.issues)}

        <TextStack spacing="tight">
          {keyValueRow('Recommended action', payload.recommended_action)}
          {keyValueRow(
            'Pointer',
            payload.timesheet.stripe_invoice_item_id || 'blank',
          )}
          {keyValueRow(
            'Linked invoices',
            linkedInvoiceIds.length === 1 ? (
              <Link to={routeToInvoice(linkedInvoiceIds[0])}>
                {linkedInvoiceIds[0]}
              </Link>
            ) : (
              compactList(linkedInvoiceIds, 5)
            ),
          )}
          {keyValueRow('Keeper item', payload.keeper_invoice_item_id || 'n/a')}
          {keyValueRow('Orphan item', payload.orphan_invoice_item_id || 'n/a')}
        </TextStack>

        {payload.net_time_analysis && (
          <Card>
            <Card.Section>
              <TextStack spacing="tight">
                <Body weight="semibold">Net Time Analysis</Body>
                <Small>
                  expected {payload.net_time_analysis.expected_net_time}m •
                  actual {payload.net_time_analysis.actual_net_time}m •{' '}
                  {payload.net_time_analysis.status}
                  {payload.net_time_analysis.direction !== 'in_sync'
                    ? ` (${payload.net_time_analysis.direction})`
                    : ''}
                </Small>
              </TextStack>
            </Card.Section>
          </Card>
        )}

        <Card>
          <Card.Section>
            <Body weight="semibold">Active Invoice Items</Body>
          </Card.Section>
          <Card.Section>
            <Stack vertical gap={10}>
              {payload.active_invoice_items.map((item) => (
                <TextStack key={`active-item-${item.id}`} spacing="tight">
                  <Small>item {item.id}</Small>
                  <Small>
                    invoice{' '}
                    {item.invoice_id ? (
                      <Link to={routeToInvoice(item.invoice_id)}>
                        {item.invoice_id}
                      </Link>
                    ) : (
                      'n/a'
                    )}
                  </Small>
                  <Small>
                    stripe item {item.stripe_invoice_item_id || 'blank'}
                  </Small>
                  <Small>
                    stripe invoice {item.stripe_invoice_id || 'blank'}
                  </Small>
                  <Small>net time {item.net_time ?? 'n/a'}m</Small>
                </TextStack>
              ))}
            </Stack>
          </Card.Section>
        </Card>
      </Stack>
    );
  };

  const renderInvoiceDetail = (payload: InvoiceAnomaly) => (
    <Stack vertical gap={16}>
      <TextStack spacing="tight">
        <Body weight="semibold">Invoice {payload.invoice.id}</Body>
        <Small>
          Account {payload.invoice.account_id || 'n/a'} • Customer{' '}
          {payload.invoice.customer_id || 'n/a'}
        </Small>
      </TextStack>

      {statusChip(payload.status)}
      {issueChips(payload.issues)}

      <TextStack spacing="tight">
        {keyValueRow(
          'Stripe invoice id',
          payload.invoice.stripe_invoice_id || 'blank',
        )}
        {keyValueRow('Local items', payload.local_invoice_items.length)}
        {keyValueRow('Stripe items', payload.stripe_invoice_items.length)}
        {keyValueRow('Net time mismatches', payload.net_time_anomalies.length)}
        {keyValueRow(
          'Minor variances',
          payload.net_time_minor_variances.length,
        )}
      </TextStack>

      {(payload.net_time_anomalies.length > 0 ||
        payload.net_time_minor_variances.length > 0) && (
        <Card>
          <Card.Section>
            <Body weight="semibold">Discrepancy Breakdown</Body>
          </Card.Section>
          <Card.Section>
            <Stack vertical gap={10}>
              {payload.net_time_anomalies.map((entry) => (
                <Small key={`major-${entry.timesheet_id}`}>
                  Timesheet {entry.timesheet_id}: expected{' '}
                  {entry.expected_net_time}m / actual {entry.actual_net_time}m /{' '}
                  {entry.direction} by {Math.abs(entry.difference_minutes)}m
                </Small>
              ))}
              {payload.net_time_minor_variances.map((entry) => (
                <Small key={`minor-${entry.timesheet_id}`}>
                  Timesheet {entry.timesheet_id}: expected{' '}
                  {entry.expected_net_time}m / actual {entry.actual_net_time}m /{' '}
                  variance {Math.abs(entry.difference_minutes)}m
                </Small>
              ))}
            </Stack>
          </Card.Section>
        </Card>
      )}

      <Card>
        <Card.Section>
          <Body weight="semibold">Sync Coverage</Body>
        </Card.Section>
        <Card.Section>
          <TextStack spacing="tight">
            {keyValueRow(
              'Stripe items missing locally',
              compactList(payload.stripe_items_missing_locally, 6),
            )}
            {keyValueRow(
              'Local items missing in Stripe',
              compactList(payload.local_items_missing_in_stripe, 6),
            )}
            {keyValueRow(
              'Duplicate local invoices',
              payload.duplicate_local_invoices.length === 0
                ? 'none'
                : compactList(
                    payload.duplicate_local_invoices.map((item) => item.id),
                    5,
                  ),
            )}
          </TextStack>
        </Card.Section>
      </Card>

      <Card>
        <Card.Section>
          <Body weight="semibold">Local Invoice Items</Body>
        </Card.Section>
        <Card.Section>
          <Stack vertical gap={10}>
            {payload.local_invoice_items.slice(0, 8).map((item) => (
              <TextStack key={`local-item-${item.id}`} spacing="tight">
                <Small>item {item.id}</Small>
                <Small>timesheet {item.timesheet_id || 'n/a'}</Small>
                <Small>
                  stripe item {item.stripe_invoice_item_id || 'blank'}
                </Small>
                <Small>net time {item.net_time ?? 'n/a'}m</Small>
              </TextStack>
            ))}
            {payload.local_invoice_items.length > 8 && (
              <Small>
                Showing first 8 of {payload.local_invoice_items.length} local
                items
              </Small>
            )}
          </Stack>
        </Card.Section>
      </Card>

      <Card>
        <Card.Section>
          <Body weight="semibold">Stripe Invoice Items</Body>
        </Card.Section>
        <Card.Section>
          <Stack vertical gap={10}>
            {payload.stripe_invoice_items.slice(0, 8).map((item) => (
              <TextStack key={`stripe-item-${item.id}`} spacing="tight">
                <Small>{item.id}</Small>
                <Small>invoice {item.invoice || 'blank'}</Small>
                <Small>amount {item.amount ?? 'n/a'}</Small>
                <Small>{item.description || 'no description'}</Small>
              </TextStack>
            ))}
            {payload.stripe_invoice_items.length > 8 && (
              <Small>
                Showing first 8 of {payload.stripe_invoice_items.length} Stripe
                items
              </Small>
            )}
          </Stack>
        </Card.Section>
      </Card>
    </Stack>
  );

  const renderDuplicatePairDetail = (payload: CandidateDuplicatePair) => (
    <Stack vertical gap={16}>
      <TextStack spacing="tight">
        <Body weight="semibold">
          Duplicate Pair {payload.left_invoice_id} / {payload.right_invoice_id}
        </Body>
        <Small>
          Use this to compare the likely duplicate invoice with its keeper.
        </Small>
      </TextStack>

      {payload.fully_covered ? (
        <Chip color="warning" label="Fully Covered" size="small" />
      ) : (
        <Chip color="default" label="Partial Overlap" size="small" />
      )}

      <TextStack spacing="tight">
        {keyValueRow(
          'Duplicate candidate',
          payload.duplicate_invoice_id ? (
            <Link to={routeToInvoice(payload.duplicate_invoice_id)}>
              {payload.duplicate_invoice_id}
            </Link>
          ) : (
            'n/a'
          ),
        )}
        {keyValueRow(
          'Keeper candidate',
          payload.keeper_invoice_id ? (
            <Link to={routeToInvoice(payload.keeper_invoice_id)}>
              {payload.keeper_invoice_id}
            </Link>
          ) : (
            'n/a'
          ),
        )}
        {keyValueRow('Shared Stripe items', payload.shared_stripe_item_count)}
        {keyValueRow('Left only', payload.left_only_count)}
        {keyValueRow('Right only', payload.right_only_count)}
      </TextStack>
    </Stack>
  );

  const renderDuplicateGroupDetail = (payload: DuplicateInvoiceGroup) => (
    <Stack vertical gap={16}>
      <TextStack spacing="tight">
        <Body weight="semibold">Duplicate Local Invoice Group</Body>
        <Small>{payload.stripe_invoice_id}</Small>
      </TextStack>
      {issueChips(['duplicate_local_invoice_rows'])}
      <TextStack spacing="tight">
        {keyValueRow('Local invoices', compactList(payload.invoice_ids, 8))}
        {keyValueRow('Accounts', compactList(payload.account_ids, 5))}
        {keyValueRow('Customers', compactList(payload.customer_ids, 5))}
        {keyValueRow('Statuses', compactList(payload.statuses, 5))}
      </TextStack>
    </Stack>
  );

  const renderDuplicateItemGroupDetail = (
    payload: DuplicateInvoiceItemGroup,
  ) => (
    <Stack vertical gap={16}>
      <TextStack spacing="tight">
        <Body weight="semibold">Duplicate Invoice Item Group</Body>
        <Small>{payload.stripe_invoice_item_id}</Small>
      </TextStack>
      {issueChips(['duplicate_local_invoice_items'])}
      <TextStack spacing="tight">
        {keyValueRow('Invoice items', compactList(payload.invoice_item_ids, 8))}
        {keyValueRow('Invoices', compactList(payload.invoice_ids, 8))}
        {keyValueRow('Timesheets', compactList(payload.timesheet_ids, 8))}
      </TextStack>
    </Stack>
  );

  if (loading && !data) {
    return <LoadingState text="Loading invoice health..." />;
  }

  return (
    <Page title="Invoice Health" size="xl">
      <Stack vertical gap={24}>
        <Card>
          <Card.Section>
            <Stack vertical gap={16}>
              <div>
                <Heading>Invoice Health</Heading>
                <Small>
                  Monitor timesheet pointer mismatches, invoice sync failures,
                  duplicate invoices, and billed-time discrepancies across local
                  and Stripe data.
                </Small>
                <br />
                <Small style={{ color: '#475467' }}>
                  Read-only dashboard. It does not delete, void, relink, or
                  rerun invoices without an explicit follow-up action outside
                  this page.
                </Small>
              </div>

              <Stack gap={12} align="center" wrap>
                <Chip
                  color={summaryTone.color}
                  label={`Health ${summaryTone.label}`}
                  size="small"
                />
                <Small>Last updated {formatTimestamp(lastUpdated)}</Small>
                <Small>
                  Active filters {activeFilterCount}
                  {loading && data ? ' • Refreshing…' : ''}
                </Small>
              </Stack>

              <Stack gap={12} wrap>
                <div style={{ minWidth: 160, flex: 1 }}>
                  <TextInput
                    id="invoice-health-account"
                    placeholder="Account ID"
                    value={filters.accountId}
                    onChange={(event) =>
                      onFilterChange('accountId', event.target.value)
                    }
                  />
                </div>
                <div style={{ minWidth: 160, flex: 1 }}>
                  <TextInput
                    id="invoice-health-customer"
                    placeholder="Customer ID"
                    value={filters.customerId}
                    onChange={(event) =>
                      onFilterChange('customerId', event.target.value)
                    }
                  />
                </div>
                <div style={{ minWidth: 160, flex: 1 }}>
                  <TextInput
                    id="invoice-health-timesheet"
                    placeholder="Timesheet ID"
                    value={filters.timesheetId}
                    onChange={(event) =>
                      onFilterChange('timesheetId', event.target.value)
                    }
                  />
                </div>
                <div style={{ minWidth: 160, flex: 1 }}>
                  <TextInput
                    id="invoice-health-invoice"
                    placeholder="Invoice ID"
                    value={filters.invoiceId}
                    onChange={(event) =>
                      onFilterChange('invoiceId', event.target.value)
                    }
                  />
                </div>
                <div style={{ minWidth: 180, flex: 1 }}>
                  <TextInput
                    id="invoice-health-from"
                    placeholder="From date or ISO8601"
                    value={filters.from}
                    onChange={(event) =>
                      onFilterChange('from', event.target.value)
                    }
                  />
                </div>
                <div style={{ minWidth: 180, flex: 1 }}>
                  <TextInput
                    id="invoice-health-to"
                    placeholder="To date or ISO8601"
                    value={filters.to}
                    onChange={(event) =>
                      onFilterChange('to', event.target.value)
                    }
                  />
                </div>
                <div style={{ minWidth: 120, flex: 1 }}>
                  <TextInput
                    id="invoice-health-limit"
                    placeholder="Limit"
                    value={filters.limit}
                    onChange={(event) =>
                      onFilterChange('limit', event.target.value)
                    }
                  />
                </div>
              </Stack>

              <Stack gap={12} wrap>
                <Button
                  a11yLabel="Run invoice health analysis"
                  label="Run Analysis"
                  isLoading={loading && !!data}
                  onClick={fetchSummary}
                />
                <IconicButton
                  a11yLabel="Refresh invoice health"
                  disabled={loading && !!data}
                  icon={faArrowRotateRight}
                  text="Refresh"
                  onClick={fetchSummary}
                />
                <Button
                  a11yLabel="Clear invoice health filters"
                  appearance="secondary"
                  disabled={loading}
                  label="Clear Filters"
                  onClick={resetFilters}
                />
              </Stack>

              {error && <Small style={{ color: '#B42318' }}>{error}</Small>}
            </Stack>
          </Card.Section>
        </Card>

        {data && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '12px',
              }}
            >
              {SUMMARY_CARDS.map((card) => (
                <Card key={card.key}>
                  <Card.Section>
                    <Stack vertical gap={8}>
                      <Small>{card.label}</Small>
                      <Stack gap={8} align="center">
                        <Icon fixedWidth icon={card.icon} />
                        <Heading>{String(data.summary[card.key])}</Heading>
                      </Stack>
                    </Stack>
                  </Card.Section>
                </Card>
              ))}
            </div>

            <Card>
              <Card.Section>
                <Tabs
                  tabs={TABS}
                  selected={activeTab}
                  onSelect={setActiveTab}
                  wrapperStyle={{ padding: 0 }}
                />
              </Card.Section>
            </Card>

            <Stack
              gap={24}
              align="stretch"
              wrap={phoneOnly}
              css={{ flexDirection: phoneOnly ? 'column' : 'row' }}
            >
              <div style={{ flex: 2, minWidth: 0 }}>
                {selectedTab === 'overview' && renderOverview()}
                {selectedTab === 'timesheets' && renderTimesheets()}
                {selectedTab === 'invoices' && renderInvoices()}
                {selectedTab === 'duplicates' && renderDuplicates()}
              </div>

              <div style={{ flex: 1, minWidth: phoneOnly ? '100%' : 320 }}>
                <Card>
                  <Card.Section>
                    <Heading>Inspection Detail</Heading>
                    <Small>
                      Structured context for the currently selected anomaly.
                    </Small>
                  </Card.Section>
                  <Card.Section>
                    {!detail.payload && (
                      <EmptyState title="Select an anomaly to inspect" />
                    )}

                    {detail.kind === 'timesheet' &&
                      detail.payload &&
                      renderTimesheetDetail(detail.payload)}

                    {detail.kind === 'invoice' &&
                      detail.payload &&
                      renderInvoiceDetail(detail.payload)}

                    {detail.kind === 'duplicate-pair' &&
                      detail.payload &&
                      renderDuplicatePairDetail(detail.payload)}

                    {detail.kind === 'duplicate-group' &&
                      detail.payload &&
                      renderDuplicateGroupDetail(detail.payload)}

                    {detail.kind === 'duplicate-item-group' &&
                      detail.payload &&
                      renderDuplicateItemGroupDetail(detail.payload)}
                  </Card.Section>
                </Card>
              </div>
            </Stack>
          </>
        )}
      </Stack>
    </Page>
  );
};

export default InvoiceHealth;
